import { FocusTrap } from 'focus-trap-react';
import { Fragment, h } from 'preact';
import { useCallback, useMemo } from 'preact/hooks';
import { USER_CLICK, USER_KEYBOARD } from '../../../../../types';
import { usePlayerContext, useStyles } from '../../../../common/hooks';
import { connect, Delayed, withBreakPoints } from '../../../hoc';
import { isKeyBackward, isKeyForward } from '../../../utils/focusTrap';
import { Button } from '../../common';
import { PANEL_CLOSE_BUTTON, PANEL_CLOSE_BUTTON_SIZE, PANEL_CLOSE_LABEL, PANEL_LIVE_OPTION_WRAPPER_STYLE_OPENED, PANEL_TITLE } from './styles';

import ProgramTypeSwitch from '../../zap/ProgramTypeSwitch';

import { isValidateKeyboard } from '../../../../common/utils/keyboard';
import { TRANSITION_DURATION } from '../../../theme/colors';
import '../../../theme/styles/panel.css';
import { togglePanel } from '../../../utils';
import Programs from '../../zap/Programs';
import Chat from './Chat';
import { CHAT_NAME, HIGHLIGHTS_NAME, INFOS_NAME, PLAYLIST_NAME, RECO_NAME, ZAPPING_NAME } from './constants';
import InfosPanel from './InfosPanel';

const TITLES_MAPPING = {
  [ZAPPING_NAME]: 'Tous les directs',
  [HIGHLIGHTS_NAME]: 'Les temps forts',
  [CHAT_NAME]: 'Live chat',
  [PLAYLIST_NAME]: 'Du même programme',
  [RECO_NAME]: 'Recommandé pour vous',
  [INFOS_NAME]: 'Infos'
};

export function usePanelCloseCallback() {
  const player = usePlayerContext();

  return useCallback((name, fromKeyboard) => {
    togglePanel(
      player,
      { show: false, source: fromKeyboard ? USER_KEYBOARD : USER_CLICK, name }
    );
  });
}

function PanelLiveOption({ panelLiveOption, smallContainer, panelHeight, isPortrait }) {
  const setStyles = useStyles();

  const close = usePanelCloseCallback();

  const panelWrapperStyle = useMemo(() => {
    if (smallContainer) {
      return {
        height: '100%',
        width: '100%',
        position: 'absolute',
        top: 0,
        borderRadius: 0
      };
    }

    if (isPortrait) {
      return {
        position: 'absolute',
        width: '100%',
        height: panelHeight || '100%',
        bottom: 0
      };
    }

    return {
      top: 0,
      height: panelHeight || '100%'
    };
  }, [smallContainer, isPortrait, panelHeight]);

  const chooseComponent = (name) => {
    switch (name) {
      case CHAT_NAME:
        return <Chat />;
      case INFOS_NAME:
        return <InfosPanel />;
      default:
        return (
          <Fragment>
            <ProgramTypeSwitch />
            <Programs />
          </Fragment>
        );
    }
  };

  const onClickClose = useCallback((e) => {
    if (e instanceof PointerEvent) {
      close(panelLiveOption.currentTab, false);
    }
  }, [close, panelLiveOption.currentTab]);

  const onKeyDownClose = useCallback((e) => {
    if (isValidateKeyboard(e)) {
      close(panelLiveOption.currentTab, true);
    }
  }, [close, panelLiveOption.currentTab]);

  const closeStylesOverride = useMemo(() => setStyles(PANEL_CLOSE_BUTTON), [setStyles]);
  const closeLabelStyle = useMemo(() => setStyles(PANEL_CLOSE_LABEL), [setStyles]);
  const size = useMemo(() => setStyles(PANEL_CLOSE_BUTTON_SIZE).width, [setStyles]);

  return (
    <Delayed isMounted={panelLiveOption.show} shouldDelay delay={TRANSITION_DURATION}>
      {() => (
        <FocusTrap
          active={panelLiveOption.show}
          focusTrapOptions={{
            clickOutsideDeactivates: true,
            returnFocusOnDeactivate: false,
            isKeyForward,
            isKeyBackward
          }}
        >
          <div
            style={{
              ...setStyles(PANEL_LIVE_OPTION_WRAPPER_STYLE_OPENED),
              ...panelWrapperStyle,
              animationTimingFunction: 'ease-in-out',
              animation: panelLiveOption.show
                ? `ftv-magneto-panel-live-option-show${(isPortrait && '-portrait') || ''} ${TRANSITION_DURATION}ms`
                : `ftv-magneto-panel-live-option-hide${(isPortrait && '-portrait') || ''} ${TRANSITION_DURATION}ms`
            }}
            id="panel-live-option"
            className="ftv-magneto--panel"
          >
            <Button
              type="icon"
              openingMode="click"
              label="Fermer"
              ariaLabel="Fermer"
              forceLabel
              disableFocus={false}
              last
              onClick={onClickClose}
              onKeyDown={onKeyDownClose}
              icon="close"
              xs
              iconRef={null}
              name="btn-close-panel-live-options"
              ariaHidden={false}
              size={size}
              zone="bottom"
              styleOverride={closeStylesOverride}
              labelStyle={closeLabelStyle}
            />
            <div
              id="panel"
              tabIndex={-1} /* allow element to take clic focus without being an a11y target */
              style={{
                height: (!smallContainer && panelHeight) || '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={setStyles(PANEL_TITLE)}>
                {
                  TITLES_MAPPING[panelLiveOption.currentTab]
                  || TITLES_MAPPING[ZAPPING_NAME]
                }
              </div>
              {
                chooseComponent(panelLiveOption.currentTab)
              }
            </div>
          </div>
        </FocusTrap>
      )}
    </Delayed>
  );
}

export default withBreakPoints(connect(({ ui: { isPortrait } }) => ({ isPortrait }))(PanelLiveOption));
